import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Tooltip } from '@/components/ui';
import { buildDailyHeatmap, CHART_MONTHS_RU, type HeatmapCell } from './chartData';
import type { ChartState } from './chartPalette';
import { ChartStatus, ChartTable, ChartTooltipContent } from './ChartParts';

export interface DailyHeatmapProps {
  label: string;
  from: string;
  to: string;
  values: Readonly<Record<string, number | null>>;
  domainMax?: number;
  formatValue?: (value: number) => string;
  unit?: string;
  state?: ChartState;
  onRetry?: () => void;
}
const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const exactNumber = (value: number) => String(value).replace('.', ',').replace(/^-/, '−');
function dateLabel(iso: string) {
  return dateFormat.format(new Date(iso + 'T12:00:00Z'));
}
function HeatCell({ level, pattern }: { level: HeatmapCell['level']; pattern: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="block size-full overflow-visible rounded-xs">
      <rect
        x="1"
        y="1"
        width="22"
        height="22"
        rx="3"
        fill={level === null ? 'url(#' + pattern + ')' : level === 0 ? 'var(--muted)' : 'var(--chart-2)'}
        fillOpacity={level === null || level === 0 ? 1 : level / 4}
        stroke={level === null ? 'var(--fg-subtle)' : 'var(--line)'}
        strokeDasharray={level === null ? '2 2' : undefined}
      />
    </svg>
  );
}
export function DailyHeatmap({
  label,
  from,
  to,
  values,
  domainMax,
  formatValue = exactNumber,
  unit = 'шт.',
  state = 'ready',
  onRetry,
}: DailyHeatmapProps) {
  const id = useId().replaceAll(':', ''),
    refs = useRef(new Map<string, HTMLButtonElement>());
  const [active, setActive] = useState<string | null>(null),
    [selected, setSelected] = useState<string | null>(null);
  const model = buildDailyHeatmap(from, to, values, domainMax);
  const days = model.weeks.flat().filter((c): c is HeatmapCell => c !== null),
    dates = days.map((c) => c.date);
  const mode =
    state !== 'ready'
      ? state
      : model.error
        ? 'error'
        : !days.some((c) => c.value !== null)
          ? 'empty'
          : 'ready';
  if (mode !== 'ready')
    return (
      <figure aria-label={label} data-slot="daily-heatmap">
        <ChartStatus
          state={mode}
          kind="heat"
          error={model.error}
          onRetry={onRetry}
          columns={model.weeks.length || 18}
        />
      </figure>
    );
  const current = dates.includes(active ?? '') ? active : dates[0],
    chosen = days.find((c) => c.date === selected);
  const text = (v: number | null) => (v === null ? 'Нет данных' : formatValue(v) + ' ' + unit);
  const move = (e: KeyboardEvent<HTMLButtonElement>, cell: HeatmapCell, weekday: number) => {
    const i = dates.indexOf(cell.date);
    const next =
      e.key === 'ArrowRight'
        ? i + 7
        : e.key === 'ArrowLeft'
          ? i - 7
          : e.key === 'ArrowDown'
            ? i + 1
            : e.key === 'ArrowUp'
              ? i - 1
              : e.key === 'Home'
                ? e.ctrlKey
                  ? 0
                  : i - weekday
                : e.key === 'End'
                  ? e.ctrlKey
                    ? dates.length - 1
                    : i + 6 - weekday
                  : null;
    if (next === null) return;
    e.preventDefault();
    const node = refs.current.get(dates[Math.max(0, Math.min(dates.length - 1, next))]);
    const origin = e.currentTarget;
    node?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    requestAnimationFrame(() => {
      if (document.activeElement === origin) node?.focus({ preventScroll: true });
    });
  };
  return (
    <figure aria-labelledby={id} data-slot="daily-heatmap" className="min-w-0 space-y-4">
      <figcaption id={id} className="sr-only">
        {label}
      </figcaption>
      <svg aria-hidden width="0" height="0" className="absolute">
        <defs>
          <pattern id={id + '-hatch'} width="6" height="6" patternUnits="userSpaceOnUse">
            <path d="M-1 1L1 -1M0 6L6 0M5 7L7 5" stroke="var(--fg-subtle)" strokeWidth="1" opacity=".4" />
          </pattern>
        </defs>
      </svg>
      <div className="min-w-0 overflow-x-auto pb-2" data-chart-scroll="">
        <div role="grid" aria-label={label + ' — дни'} aria-describedby={id + '-help'} className="w-max">
          <div aria-hidden className="mb-2 flex h-5 items-center gap-1 text-2xs text-fg-subtle">
            <span className="w-8 shrink-0" />
            {model.weeks.map((week, i) => {
              const first = week.find((c) => c !== null);
              const previous = i ? model.weeks[i - 1].find((c) => c !== null) : null;
              const show = first && (!previous || previous.date.slice(0, 7) !== first.date.slice(0, 7));
              return (
                <span key={i} className="w-6 shrink-0 whitespace-nowrap">
                  {show ? CHART_MONTHS_RU[Number(first.date.slice(5, 7)) - 1] : ''}
                </span>
              );
            })}
          </div>
          {weekdays.map((day, row) => (
            <div role="row" key={day} className="mb-1 flex items-center gap-1 last:mb-0">
              <span role="rowheader" className="w-8 shrink-0 text-2xs text-fg-subtle">
                {day}
              </span>
              {model.weeks.map((week, col) => {
                const cell = week[row];
                return (
                  <div role="gridcell" key={col} className="size-6 shrink-0">
                    {cell && (
                      <Tooltip
                        variant="data"
                        content={
                          <ChartTooltipContent
                            title={dateLabel(cell.date)}
                            rows={[{ label: 'Значение', value: text(cell.value) }]}
                          />
                        }
                      >
                        <button
                          type="button"
                          ref={(node) => {
                            if (node) refs.current.set(cell.date, node);
                            else refs.current.delete(cell.date);
                          }}
                          data-heat-date={cell.date}
                          data-heat-value={cell.value ?? 'missing'}
                          data-heat-level={cell.level ?? 'missing'}
                          aria-label={dateLabel(cell.date) + ': ' + text(cell.value)}
                          tabIndex={cell.date === current ? 0 : -1}
                          onFocus={() => {
                            setActive(cell.date);
                            setSelected(cell.date);
                          }}
                          onClick={() => setSelected(cell.date)}
                          onKeyDown={(e) => move(e, cell, row)}
                          className="block size-6 rounded-xs transition-opacity duration-fast hover:opacity-70 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset"
                        >
                          <HeatCell level={cell.level} pattern={id + '-hatch'} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-2 text-2xs text-fg-muted"
        aria-label="Шкала нагрузки"
      >
        <span className="inline-flex items-center gap-1.5">
          <span className="size-4">
            <HeatCell level={0} pattern={id + '-hatch'} />
          </span>
          0 {unit}
        </span>
        <span className="inline-flex flex-wrap items-center gap-1">
          <span className="mr-1">Меньше</span>
          {([1, 2, 3, 4] as const).map((n) => (
            <span key={n} aria-hidden className="size-4">
              <HeatCell level={n} pattern={id + '-hatch'} />
            </span>
          ))}
          <span className="ml-1">
            {formatValue(model.max)} {unit} и больше
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-4">
            <HeatCell level={null} pattern={id + '-hatch'} />
          </span>
          Нет данных
        </span>
      </div>
      <div className="min-h-16 rounded-md bg-muted px-3 py-2 text-xs text-fg-muted" data-chart-selection="">
        {chosen ? (
          <>
            <p className="font-medium text-fg">{dateLabel(chosen.date)}</p>
            <p className="mt-1 tabular-nums">{text(chosen.value)}</p>
          </>
        ) : (
          <p>Наведи на день или выбери его с клавиатуры.</p>
        )}
      </div>
      <p id={id + '-help'} className="text-2xs text-fg-subtle">
        ← → — неделя, ↑ ↓ — день. Home / End — границы недели, с Ctrl — всего периода.
      </p>
      <ChartTable
        label={label}
        headers={['Дата', 'Значение, ' + unit]}
        rows={days.map((c) => [dateLabel(c.date), c.value === null ? 'Нет данных' : formatValue(c.value)])}
      />
    </figure>
  );
}
